/**
 * Godot's texture importer (`editor/import/resource_importer_texture.cpp`): the sidecar options that
 * change an image file's texture, and the format the texture holds after import.
 */
import { storesAlpha } from './imageSourceFormat.js';
import { enumMember } from '../classdb/enumNames.js';
/** The importer a sidecar names for a 2D texture (`resource_importer_texture.cpp:164-166`). */
export const TEXTURE_IMPORTER = 'texture';
/**
 * `compress/mode`, indexed by the number a sidecar stores (`resource_importer_texture.cpp:234`).
 * `save_to_ctex_format` writes no texture for a number outside it (`:275-352`). `compressMode`
 * reads such a number as the default, so the preview shows the image rather than nothing.
 */
export const TEXTURE_COMPRESS_MODES = [
    'Lossless',
    'Lossy',
    'VramCompressed',
    'VramUncompressed',
    'BasisUniversal',
];
/** `compress/normal_map` (`resource_importer_texture.cpp:242`). */
export const TextureNormalMapMode = {
    Detect: 0,
    Enable: 1,
    Disabled: 2,
};
/** `Image::RoughnessChannel` (`core/io/image.h:153-159`): the channel the roughness limiter raises. */
export const RoughnessChannel = {
    R: 0,
    G: 1,
    B: 2,
    A: 3,
    /** The grey of red, green and blue. */
    L: 4,
};
/** `roughness/mode` from Red on, as `Image::RoughnessChannel` (`resource_importer_texture.cpp:247`, `:783`). */
const ROUGHNESS_MODE_FIRST_CHANNEL = 2;
/**
 * The roughness limiter a `roughness/mode` and `roughness/src_normal` ask for, or null for Detect,
 * Disabled, a mode outside the enum or no normal map (`resource_importer_texture.cpp:777-786`).
 */
export function roughnessLimitOption(mode, normalPath) {
    if (mode === undefined || !normalPath)
        return null;
    const channel = enumMember(RoughnessChannel, mode - ROUGHNESS_MODE_FIRST_CHANNEL);
    return channel === null ? null : { channel, normalPath };
}
/**
 * The defaults of the importer's Detect preset, which a fresh import writes and a file with no
 * sidecar reads as. The editor's later re-imports of a texture it sees in 3D or as a normal map are
 * not predicted.
 */
export const DEFAULT_TEXTURE_IMPORT = {
    mode: 'Lossless',
    highQuality: false,
    normalMap: false,
    mipmaps: false,
    fixAlphaBorder: true,
    premultAlpha: false,
    invertNormalY: false,
    svgScale: 1,
    roughnessLimit: null,
};
/**
 * The `Image::format_names` entry the imported texture holds on a desktop GPU with S3TC and BPTC.
 * Lossless, Lossy and VRAM Uncompressed keep the source format: a WebP-packed image converts back to
 * it on load (`compressed_texture.cpp:345-352`). `isTranslucent` reads the texels, and only S3TC
 * of a source with alpha calls it.
 */
export function textureImportFormat(source, options, isTranslucent) {
    if (holdsRedGreenOnly(options))
        return RED_GREEN_FORMAT;
    switch (options.mode) {
        case 'VramCompressed':
            return options.highQuality ? 'BPTC_RGBA' : s3tcFormat(source, isTranslucent);
        case 'BasisUniversal':
            // Transcoded to BC7, which a desktop GPU supports (`image_compress_basisu.cpp:356-393`).
            return 'BPTC_RGBA';
        default:
            return source;
    }
}
/** RGTC with two channels, which a shader samples as `(r, g, 0, 1)` and which has no sRGB view. */
const RED_GREEN_FORMAT = 'RGTC RedGreen8';
/**
 * Whether the import keeps only red and green. A normal map uses only RG (`core/io/image.cpp:3732-3734`),
 * so VRAM compression without high quality and Basis Universal, transcoded to BC5
 * (`image_compress_basisu.cpp:321-324`), store it as RGTC.
 */
export function holdsRedGreenOnly({ mode, highQuality, normalMap }) {
    if (!normalMap)
        return false;
    return mode === 'BasisUniversal' || (mode === 'VramCompressed' && !highQuality);
}
/**
 * S3TC by the channels the texels use (`resource_importer_texture.cpp:423-431`,
 * `image_compress_etcpak.cpp:60-78`): DXT5 when a texel is translucent, else DXT1. The default
 * sRGB-friendly channel pack turns red-only and red-green into RGB (`core/io/image.cpp:3800-3802`).
 */
function s3tcFormat(source, isTranslucent) {
    return storesAlpha(source) && isTranslucent() ? 'DXT5 RGBA8' : 'DXT1 RGB8';
}
/** `Image::detect_used_channels` counts a texel with alpha below this as using alpha (`core/io/image.cpp:3760`). */
const OPAQUE_ALPHA_MIN = 0.999;
/** Whether any texel of RGBA8 `bytes` uses alpha, as `Image::detect_used_channels` finds it. */
export function hasTranslucentTexel(bytes) {
    for (let alpha = 3; alpha < bytes.length; alpha += 4) {
        if (bytes[alpha] / 255 < OPAQUE_ALPHA_MIN)
            return true;
    }
    return false;
}
//# sourceMappingURL=textureImport.js.map